import { graphql } from "@acto/gql";
import { useMemo, useCallback } from "react";
import { useQuery } from "urql";
import { Customer } from "@acto/taskpane/core/domain";
import { mapClientToCustomer } from "./mapClientToCustomer";

const ClientByIdQuery = graphql(`
  query GetClientById($id: ID!) {
    clients(first: 1, filter: { id: $id }) {
      __typename
      edges {
        __typename
        node {
          ...ClientForDetailsWithRecommendedObservation
        }
      }
    }
  }
`);

export interface SingleDataAccessResult<T> {
  data?: T;
  isPending: boolean;
  isFetching: boolean;
  error: Error | undefined;
  refetch?: () => void;
}

export function useCustomerById(id: string): SingleDataAccessResult<Customer> {
  const [result, reexecuteQuery] = useQuery({
    query: ClientByIdQuery,
    variables: { id },
    pause: !id,
    requestPolicy: "cache-first",
  });

  const customer = useMemo(() => {
    if (!result.data?.clients?.edges?.length) return undefined;

    const clientNode = result.data.clients.edges[0].node;
    return mapClientToCustomer(clientNode);
  }, [result.data]);

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  return {
    data: customer,
    isFetching: result.fetching,
    isPending: result.fetching && !result.data,
    error: result.error,
    refetch,
  };
}
